import React, { useState, useEffect, useRef, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import { Coffee, UtensilsCrossed, Wine } from 'lucide-react';
import ReactGA from "react-ga4";
import './MapView.css';
import { FiExternalLink } from 'react-icons/fi';
import { secureFetch } from '../utils/secureApi';

// Marker styles configuration
const markerStyles = {
    restaurant: {
        path: 'M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0',
        fillColor: '#FF5252',
        strokeColor: '#D32F2F',
        scale: 0.4
    },
    bar: {
        path: 'M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0',
        fillColor: '#7C4DFF',
        strokeColor: '#651FFF',
        scale: 0.4
    },
    cafe: {
        path: 'M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0',  
        fillColor: '#FF9800',
        strokeColor: '#F57C00',
        scale: 0.4
    },
    default: {
        path: 'M-10,0a10,10 0 1,0 20,0a10,10 0 1,0 -20,0',
        fillColor: '#757575',
        strokeColor: '#616161',
        scale: 0.5
    },
    userLocation: {
        path: 'M16,0 C16,-8 8,-16 0,-16 C-8,-16 -16,-8 -16,0 C-16,8 -8,16 0,16 C8,16 16,8 16,0 Z',
        fillColor: 'rgba(66,133,244,0.8)',
        strokeColor: '#005cbf',
        strokeWeight: 1,
        scale: 0.5,
        fillOpacity: 0.5,
    }
};
const DescriptionBox = () => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    if (!isVisible) return null;

    return (
        <div className="description-box fade-out">
            <div className="description-title">RestPocket © 2024</div>
            <div className="description-text">
                Your pocket guide to find the best restaurants, bars, and cafes nearby. 
                View menus, check locations, and discover new places.
            </div>
            <div className="description-features">
                <span>Terms</span>
                <span>·</span>
                <span>Privacy</span>
                <span>·</span>
                <span>Send feedback</span>
            </div>
        </div>
    );
};
const BottomControls = ({ selectedVenues, onToggleVenue }) => {
    return (
        <div className="bottom-controls">
            <div 
                className={`toggle-switch restaurant ${selectedVenues.includes('restaurant') ? 'active' : ''}`}
                onClick={() => onToggleVenue('restaurant')}
                title="Restaurants"
            >
                <div className="toggle-track">
                    <div className="toggle-slider">
                        <UtensilsCrossed size={16} />
                    </div>
                </div>
            </div>

            <div 
                className={`toggle-switch bar ${selectedVenues.includes('bar') ? 'active' : ''}`}
                onClick={() => onToggleVenue('bar')}
                title="Bars"
            >
                <div className="toggle-track">
                    <div className="toggle-slider">
                        <Wine size={16} />
                    </div>
                </div>
            </div>

            <div 
                className={`toggle-switch cafe ${selectedVenues.includes('cafe') ? 'active' : ''}`}
                onClick={() => onToggleVenue('cafe')}
                title="Cafes"
            >
                <div className="toggle-track">
                    <div className="toggle-slider">
                        <Coffee size={16} />
                    </div>  
                </div>
            </div>
        </div>
    );
};

const SidebarContent = ({ filteredRestaurants }) => {
    const adRef = useRef(null);
    const isAdInitialized = useRef(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (adRef.current && !isAdInitialized.current && window.adsbygoogle) {
            try {
                window.adsbygoogle.push({});
                isAdInitialized.current = true;
            } catch (error) {
                console.error('Error initializing ad:', error);
            }
        }
    }, []);

    if (isMobile) return null;

    const handlePlaceClick = (menuUrl) => {
        if (menuUrl) {
            window.open(menuUrl, '_blank', 'noopener,noreferrer');
        } else {
            alert('Menu URL not available');
        }
    };

    return (
        <div className="sidebar-container">
            <div className="sidebar-content">
                <h2>Nearby Places</h2>
                <div className="stats-container">
                    <div className="stat-item">
                        <strong>{filteredRestaurants.length}</strong>
                        <span>Places Found</span>
                    </div>
                    <div className="stat-item">
                        <strong>{filteredRestaurants.filter(r => r.venue_type === 'restaurant').length}</strong>
                        <span>Restaurants</span>
                    </div>
                    <div className="stat-item">
                        <strong>{filteredRestaurants.filter(r => r.venue_type === 'cafe').length}</strong>
                        <span>Cafes</span>
                    </div>
                </div>
                
                <div className="featured-places">
                    <h3>Featured Places</h3>
                    {filteredRestaurants.slice(0, 3).map(place => (
                        <div 
                            key={place._id} 
                            className="featured-place-item" 
                            onClick={() => handlePlaceClick(place.menu_url)}
                            style={{ cursor: 'pointer' }}
                        >
                            <strong>{place.restaurant_name}</strong>
                            <span>{place.cuisines_label}</span>
                            <p>{place.description || 'A great place to enjoy delicious food and drinks.'}</p>
                        </div>
                    ))}
                </div>

                <div className="ad-section">
                    <div className="ad-label">Advertisement</div>
                    <ins
                        ref={adRef}
                        className="adsbygoogle"
                        style={{
                            display: 'block',
                            width: '160px',
                            height: '600px'
                        }}
                        data-ad-client="ca-pub-2232654950546882"
                        data-ad-slot="6240806457"
                        data-ad-format="vertical"
                    />
                </div>
            </div>
        </div>
    );
};

const MenuIframe = React.memo(({ url, title, onClose }) => {
    const [isLoading, setIsLoading] = useState(true);
    const iframeRef = useRef(null);

    useEffect(() => {
        const handleLoad = () => {
            setIsLoading(false);
        };

        const iframe = iframeRef.current;
        if (iframe) {
            iframe.addEventListener('load', handleLoad);

            return () => {
                iframe.removeEventListener('load', handleLoad);
            };
        }
    }, [url]);

    return (
        <div className="iframe-container">
            <div className="iframe-header">
                <h2>{title}</h2>
                <div className="header-actions">
                    <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="open-new-tab-icon"
                        aria-label="Open in New Tab"
                    >
                        <FiExternalLink size={20} />
                    </a>
                    <button className="close-btn" onClick={onClose} aria-label="Close Menu">Close</button>
                </div>
            </div>
            
            {isLoading && (
                <div className="iframe-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading menu...</p>
                </div>
            )}

            <iframe
                ref={iframeRef}
                src={url}
                className="menu-iframe"
                title={`Menu for ${title}`}
                onLoad={() => setIsLoading(false)}
                style={{ display: isLoading ? 'none' : 'block' }}
            />
        </div>
    );
});

const MapView = () => {
    const [restaurants, setRestaurants] = useState([]);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);
    const [center, setCenter] = useState({ lat: 41.0282307, lng: 28.9877909 });
    const [zoom, setZoom] = useState(14);
    const [selectedMenuUrl, setSelectedMenuUrl] = useState('');
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const mapRef = useRef(null);
    const isInitialLoad = useRef(true);
    const [selectedCuisine, setSelectedCuisine] = useState('');
    const [selectedPriceCategory, setSelectedPriceCategory] = useState('');
    const [selectedVenues, setSelectedVenues] = useState(['restaurant', 'bar', 'cafe']);

    useEffect(() => {
        ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
    }, []);

    useEffect(() => {
        if (!window.adsbygoogle && !document.querySelector('script[src*="adsbygoogle"]')) {
            const script = document.createElement('script');
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2232654950546882';
            script.async = true;
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
            window.adsbygoogle = window.adsbygoogle || [];
        }

        return () => {
            const script = document.querySelector('script[src*="adsbygoogle"]');
            if (script) {
                script.remove();
            }
        };
    }, []);

    const handleVenueToggle = useCallback((venueType) => {
        setSelectedVenues(prev => {
            if (prev.includes(venueType)) {
                ReactGA.event({
                    category: "Venue Filter",
                    action: "Toggle Off",
                    label: venueType
                });
                return prev.filter(v => v !== venueType);
            }
            ReactGA.event({
                category: "Venue Filter",
                action: "Toggle On",
                label: venueType
            });
            return [...prev, venueType];
        });
    }, []);

    const fetchRestaurants = useCallback(async (lat, lng) => {
        try {
            const data = await secureFetch('/.netlify/functions/get-restaurants', {
                method: 'POST',
                body: JSON.stringify({ lat, lon: lng }),
            });

            setRestaurants(data);
            setFilteredRestaurants(data);
        } catch (error) {
            console.error('Error fetching restaurants:', error);
        }
    }, []);

    useEffect(() => {
        const getLocation = () => {
            if (navigator.geolocation && isInitialLoad.current) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        setCenter({ lat: latitude, lng: longitude });
                        setUserLocation({ lat: latitude, lng: longitude });
                        fetchRestaurants(latitude, longitude);
                    },
                    (error) => {
                        console.error('Error fetching location:', error);
                        fetchRestaurants(center.lat, center.lng);
                    }
                );
            } else {
                fetchRestaurants(center.lat, center.lng);
            }
            isInitialLoad.current = false;
        };

        getLocation();
    }, [center.lat, center.lng, fetchRestaurants]);

    const addUserLocationMarker = useCallback((map, maps) => {
        if (userLocation && map) {
            new maps.Marker({
                position: { lat: userLocation.lat, lng: userLocation.lng },
                map,
                icon: {
                    path: markerStyles.userLocation.path,
                    fillColor: markerStyles.userLocation.fillColor,
                    fillOpacity: 1,
                    strokeColor: markerStyles.userLocation.strokeColor,
                    strokeWeight: 2,
                    scale: markerStyles.userLocation.scale,
                },
            });
        }
    }, [userLocation]);

    useEffect(() => {
        const filterRestaurants = () => {
            let filtered = restaurants;

            if (selectedCuisine) {
                filtered = filtered.filter(restaurant => restaurant.cuisines_label === selectedCuisine);
            }

            if (selectedPriceCategory) {
                filtered = filtered.filter(restaurant => restaurant.price_category === selectedPriceCategory);
            }

            if (selectedVenues.length > 0) {
                filtered = filtered.filter(restaurant => selectedVenues.includes(restaurant.venue_type));
            }

            setFilteredRestaurants(filtered);
        };

        filterRestaurants();
    }, [selectedCuisine, selectedPriceCategory, restaurants, selectedVenues]);

    const createMarker = useCallback((restaurant, map, maps) => {
        const lat = restaurant.location.coordinates[1];
        const lng = restaurant.location.coordinates[0];
        const venueType = restaurant.venue_type || 'default';
        const style = markerStyles[venueType] || markerStyles.default;

        const marker = new maps.Marker({
            position: { lat, lng },
            map,
            icon: {
                path: style.path,
                fillColor: style.fillColor,
                fillOpacity: 1,
                strokeColor: style.strokeColor,
                strokeWeight: 1.5,
                scale: style.scale,
                anchor: new maps.Point(0, 0),
            },
            label: {
                text: map.getZoom() > 15.9 ? restaurant.restaurant_name : "",
                className: map.getZoom() > 15.9 ? 'marker-label' : '',
                color: 'black',
                fontSize: map.getZoom() > 15.9 ? '12px' : '0px',
                fontWeight: '500'
            },
        });

        let pressTimer;

        marker.addListener('mousedown', () => {
            pressTimer = setTimeout(() => {
                const googleMapsUrl = `https://maps.google.com/?q=${lat},${lng}`;
                window.open(googleMapsUrl, '_blank');
            }, 1000);
        });

        marker.addListener('click', () => {
            ReactGA.event({
                category: "Restaurant",
                action: "View Menu",
                label: restaurant.restaurant_name
            });
            setSelectedMenuUrl(restaurant.menu_url);
            setSelectedRestaurant(restaurant);
        });

        marker.addListener('mouseup', () => {
            clearTimeout(pressTimer);
        });

        marker.addListener('mouseout', () => {
            clearTimeout(pressTimer);
        });

        return marker;
    }, []);

    useEffect(() => {
        if (mapRef.current && filteredRestaurants.length > 0) {
            const { map, maps } = mapRef.current;
            const markers = [];

            filteredRestaurants.forEach(restaurant => {
                const newMarker = createMarker(restaurant, map, maps);
                markers.push(newMarker);
            });

            return () => markers.forEach(marker => marker.setMap(null));
        }
    }, [filteredRestaurants, createMarker]);

    const handleMapChange = useCallback(({ center: newCenter, zoom: newZoom }) => {
        setCenter(newCenter);
        setZoom(newZoom);
    }, []);

    useEffect(() => {
        if (selectedCuisine) {
            ReactGA.event({
                category: "Filter",
                action: "Change Cuisine",
                label: selectedCuisine
            });
        }
    }, [selectedCuisine]);

    useEffect(() => {
        if (selectedPriceCategory) {
            ReactGA.event({
                category: "Filter",
                action: "Change Price",
                label: selectedPriceCategory
            });
        }
    }, [selectedPriceCategory]);

    return (
        <div style={{ position: 'relative', height: '100vh', width: '100%' }}>
            <div className="map-wrapper">
                <div className="filter-container">
                    <select
                        className="filter-dropdown"
                        value={selectedCuisine}
                        onChange={(e) => setSelectedCuisine(e.target.value)}
                    >
                        <option value="">Cuisine</option>
                        <option value="Türk Mutfağı">Türk Mutfağı</option>
                        <option value="Kahve ve Tatlı">Kahve ve Tatlı</option>
                        <option value="Akdeniz Mutfağı">Akdeniz Mutfağı</option>
                        <option value="Geleneksel Mutfak">Geleneksel Mutfak</option>
                        <option value="Asya Modern">Asya Modern</option>
                        <option value="Deniz Ürünleri">Deniz Ürünleri</option>
                        <option value="Modern Mutfak">Modern Mutfak</option>
                        <option value="Uluslararası">Uluslararası</option>
                    </select>

                    <select
                        className="filter-dropdown"
                        value={selectedPriceCategory}
                        onChange={(e) => setSelectedPriceCategory(e.target.value)}
                    >
                        <option value="">Price</option>
                        <option value="low">Low</option>
                        <option value="mid-range">Medium</option>
                        <option value="premium">High</option>
                    </select>
                </div>

                <div style={{ height: '100%', width: '100%' }}>
                    <GoogleMapReact
                        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }}
                        center={center}
                        zoom={zoom}
                        options={{
                            mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID
                        }}
                        onChange={handleMapChange}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={({ map, maps }) => {
                            mapRef.current = { map, maps };
                            addUserLocationMarker(map, maps);
                            map.setOptions({
                                minZoom: zoom,
                                fullscreenControl: false,
                                zoomControl: false,
                            });
                        }}
                    />
                </div>

                <BottomControls 
                    selectedVenues={selectedVenues}
                    onToggleVenue={handleVenueToggle}
                />
            </div>

            {filteredRestaurants.length > 0 && (
                <SidebarContent filteredRestaurants={filteredRestaurants} />
            )}
            <DescriptionBox />

            {selectedMenuUrl && selectedRestaurant && (
                <MenuIframe url={selectedMenuUrl} title={selectedRestaurant.restaurant_name} onClose={() => {
                    setSelectedMenuUrl('');
                    setSelectedRestaurant(null);
                }} />
            )}
        </div>
    );
};

export default MapView;
